import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    MuiTable,
    MuiTableBody,
    MuiTableHead,
    Checkbox,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    MuiTableCell,
    Box,
    useTheme,
    Icon,
    Form,
    useAppDispatch,
    RootState,
    useAppSelector,
    PargodaInfoIcon,
    Grid,
    EnvTypes,
} from '../../../base';
import {
    IDashboardTaskForm,
    AlertSeverity,
    DashboardTasksFormFields,
    IDashboardTaskItem,
    IClientAssignment,
    getPriorityColor,
    TooltipPlacement,
    DashboardTaskItemFields,
} from '../../../common';
import { dashboardActions } from '../../../actions';
import {
    StyledCard,
    StyledCardHeader,
    TasksTableContainer,
    StyledHeaderTitle,
    AddButton,
    StyledTableRow,
    StyledTableHeadRow,
    CheckboxCell,
    FlagCell,
    DateCell,
    ActionsCell,
    StyledHeaderActions,
    VerticalDivider,
    ArchiveButton,
    StyledTasksIconWrapper,
    StyledTasksIcon,
    StyledTasksEmptyState,
} from './TasksCard.styles';
import EmptyTasks from './EmptyTasks';
import CreateOrEditTask from './CreateOrEditTask';
import { readableDurationStringToShortString } from '../../../common/utils/durationUtils';

const TasksCard: React.FC = () => {
    const [tasks, setTasks] = useState<Array<IClientAssignment>>([]);
    const [isArchive, setIsArchive] = useState<boolean>(false);

    const completedTasks = useAppSelector(
        (state: RootState) => state.dashboard.completedAssignments
    );

    const openTasks = useAppSelector(
        (state: RootState) => state.dashboard.assignments
    );

    useEffect(() => {
        if (isArchive) {
            setTasks(completedTasks);
        } else {
            setTasks(openTasks);
        }
    }, [completedTasks, isArchive, openTasks]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const methods = useForm<IDashboardTaskForm>({
        defaultValues: {
            [DashboardTasksFormFields.newTasks]: [],
            [DashboardTasksFormFields.editingTasks]: [],
        },
    });

    const newTasks = useFieldArray<
        IDashboardTaskForm,
        DashboardTasksFormFields.newTasks,
        'id'
    >({
        control: methods.control,
        name: DashboardTasksFormFields.newTasks,
    });

    const editingTasks = useFieldArray<
        IDashboardTaskForm,
        DashboardTasksFormFields.editingTasks,
        'id'
    >({
        control: methods.control,
        name: DashboardTasksFormFields.editingTasks,
    });

    const handleAddTaskForm = () => {
        newTasks.append({
            [DashboardTaskItemFields.id]: `new_${Date.now().toString()}`,
            [DashboardTaskItemFields.completed]: false,
            [DashboardTaskItemFields.priority]: AlertSeverity.INFO,
            [DashboardTaskItemFields.dueDate]: null,
            [DashboardTaskItemFields.title]: '',
            [DashboardTaskItemFields.duration]: '',
            [DashboardTaskItemFields.description]: '',
        });
        setIsEditMode(true);
    };

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        taskId: string
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedTaskId(taskId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTaskId(null);
    };

    const handleDeleteTask = () => {
        if (selectedTaskId) {
            dispatch(dashboardActions.deleteAssignment({ id: selectedTaskId }));
            handleMenuClose();
            updateEditMode();
        }
    };

    const handleEditTask = () => {
        if (selectedTaskId) {
            const taskToEdit = tasks.find((task) => task.id === selectedTaskId);
            if (taskToEdit) {
                const durationShortString = readableDurationStringToShortString(
                    taskToEdit.duration
                );

                const taskFormData: IDashboardTaskItem = {
                    [DashboardTaskItemFields.id]: taskToEdit.id,
                    [DashboardTaskItemFields.completed]:
                        !!taskToEdit.isCompleted,
                    [DashboardTaskItemFields.priority]:
                        taskToEdit.priority as AlertSeverity,
                    [DashboardTaskItemFields.dueDate]: taskToEdit.dueDate ?? '',
                    [DashboardTaskItemFields.title]: taskToEdit.title ?? '',
                    [DashboardTaskItemFields.duration]: durationShortString,
                    [DashboardTaskItemFields.description]:
                        taskToEdit.description ?? '',
                };
                editingTasks.append(taskFormData);
                setTasks(tasks.filter((task) => task.id !== selectedTaskId));
                setIsEditMode(true);
            }
            handleMenuClose();
        }
    };

    const toggleTaskCompletion = (taskId: string) => {
        const task = tasks.find((task) => task.id === taskId);
        if (task) {
            dispatch(
                dashboardActions.updateAssignment({
                    assignment: { id: taskId, isCompleted: !task.isCompleted },
                    isMarkAsComplete: true,
                })
            );
        }
    };

    const handleArchive = () => {
        setIsArchive(!isArchive);
    };

    const updateEditMode = () => {
        const hasNew =
            methods.getValues(DashboardTasksFormFields.newTasks)?.length > 0;
        const hasEditing =
            methods.getValues(DashboardTasksFormFields.editingTasks)?.length >
            0;
        setIsEditMode(hasNew || hasEditing);
    };

    const formatDisplayDate = (isoString: string | undefined | null) => {
        if (!isoString) return '';
        return moment.utc(isoString).format('DD/MM/YY');
    };

    return (
        <StyledCard>
            <StyledCardHeader>
                <StyledHeaderTitle>
                    <StyledTasksIconWrapper
                        edge="end"
                        size="small"
                        onClick={() => {}}
                        disabled
                    >
                        <StyledTasksIcon name={'TasksListIcon'} size={28} />
                    </StyledTasksIconWrapper>
                    <Typography variant="h6">
                        {`${t('dashboardPage.tasksTitle')} ${isArchive ? `(${t('dashboardPage.tasksArchive')})` : ''}`}
                    </Typography>
                </StyledHeaderTitle>
                <StyledHeaderActions>
                    {!isArchive && (
                        <>
                            <AddButton onClick={handleAddTaskForm}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Icon
                                        name="AddRoundedIcon"
                                        size={20}
                                        sx={{
                                            color: theme.palette.secondary.main,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.secondary.main}
                                        sx={{ ml: 0.5 }}
                                    >
                                        {t('dashboardPage.tasksAdd')}
                                    </Typography>
                                </Box>
                            </AddButton>
                            <VerticalDivider orientation="vertical" flexItem />
                        </>
                    )}
                    <ArchiveButton onClick={handleArchive}>
                        {t(
                            isArchive
                                ? 'dashboardPage.tasksOpenAssignments'
                                : 'dashboardPage.tasksArchive'
                        )}
                    </ArchiveButton>
                </StyledHeaderActions>
            </StyledCardHeader>
            <Grid
                sx={{
                    paddingTop: theme.spacing(1),
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <TasksTableContainer dir={theme.direction}>
                    {tasks.length > 0 ||
                    editingTasks.fields.length > 0 ||
                    newTasks.fields.length > 0 ? (
                        <Form
                            methods={methods}
                            onSubmit={(data) =>
                                console.log(
                                    'Form submitted (should not happen on row confirm)',
                                    data
                                )
                            }
                        >
                            <MuiTable
                                stickyHeader
                                size="small"
                                dir={theme.direction}
                            >
                                <MuiTableHead dir={theme.direction}>
                                    <StyledTableHeadRow dir={theme.direction}>
                                        <CheckboxCell padding="checkbox"></CheckboxCell>
                                        <FlagCell padding="checkbox">
                                            <PargodaInfoIcon
                                                text={t(
                                                    'dashboardPage.tasksPriorityInfo'
                                                )}
                                                placement={
                                                    TooltipPlacement.TOP_START
                                                }
                                                size={14}
                                                wrapperSx={{
                                                    paddingInlineStart:
                                                        theme.spacing(0.5),
                                                }}
                                            />
                                        </FlagCell>
                                        <DateCell
                                            isEditMode={false}
                                            align={
                                                theme.direction === 'rtl'
                                                    ? 'right'
                                                    : 'inherit'
                                            }
                                        >
                                            <Box display="flex">
                                                {t('dashboardPage.tasksDue')}
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.tasksDueInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                />
                                            </Box>
                                        </DateCell>
                                        <MuiTableCell
                                            align={
                                                theme.direction === 'rtl'
                                                    ? 'right'
                                                    : 'inherit'
                                            }
                                            sx={{ backgroundImage: 'none' }}
                                        >
                                            <Box display="flex">
                                                {t('dashboardPage.taskHeader')}
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.taskDescriptionInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                />
                                            </Box>
                                        </MuiTableCell>
                                        <MuiTableCell
                                            sx={{ backgroundImage: 'none' }}
                                            width="80px"
                                            align="right"
                                        >
                                            <Box display="flex">
                                                {t(
                                                    'dashboardPage.tasksDuration'
                                                )}
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.tasksDurationInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                />
                                            </Box>
                                        </MuiTableCell>
                                        <ActionsCell padding="checkbox"></ActionsCell>
                                    </StyledTableHeadRow>
                                </MuiTableHead>
                                <MuiTableBody dir={theme.direction}>
                                    {newTasks.fields.map((field, index) => (
                                        <CreateOrEditTask
                                            key={field.id}
                                            formId={field.id}
                                            index={index}
                                            fieldArrayName={
                                                DashboardTasksFormFields.newTasks
                                            }
                                            newTasks={newTasks}
                                            editingTasks={editingTasks}
                                            updateEditMode={updateEditMode}
                                            onCancelEdit={() => {
                                                newTasks.remove(index);
                                                updateEditMode();
                                            }}
                                        />
                                    ))}
                                    {editingTasks.fields.map((field, index) => (
                                        <CreateOrEditTask
                                            key={field.id}
                                            formId={field.id}
                                            index={index}
                                            fieldArrayName={
                                                DashboardTasksFormFields.editingTasks
                                            }
                                            newTasks={newTasks}
                                            editingTasks={editingTasks}
                                            updateEditMode={updateEditMode}
                                            onCancelEdit={() => {
                                                editingTasks.remove(index);
                                                updateEditMode();
                                            }}
                                        />
                                    ))}
                                    {[...tasks]
                                        .sort((a, b) =>
                                            moment
                                                .utc(a.dueDate)
                                                .diff(moment.utc(b.dueDate))
                                        )
                                        .map((task) => (
                                            <StyledTableRow
                                                key={task.id}
                                                dir={theme.direction}
                                                style={{
                                                    opacity: task.isCompleted
                                                        ? 0.6
                                                        : 1,
                                                }}
                                            >
                                                <CheckboxCell padding="checkbox">
                                                    <Checkbox
                                                        checked={
                                                            !!task.isCompleted
                                                        }
                                                        onChange={() =>
                                                            toggleTaskCompletion(
                                                                task.id
                                                            )
                                                        }
                                                        size="small"
                                                        disabled={isEditMode}
                                                        sx={{
                                                            padding: 0,
                                                            '& .MuiSvgIcon-root':
                                                                {
                                                                    fontSize: 18,
                                                                },
                                                        }}
                                                    />
                                                </CheckboxCell>
                                                <FlagCell padding="checkbox">
                                                    <Icon
                                                        name="OutlinedFlagIcon"
                                                        size={20}
                                                        sx={{
                                                            color: getPriorityColor(
                                                                task.priority ??
                                                                    AlertSeverity.INFO
                                                            ),
                                                        }}
                                                    />
                                                </FlagCell>
                                                <DateCell
                                                    isEditMode={false}
                                                    align={
                                                        theme.direction ===
                                                        'rtl'
                                                            ? 'right'
                                                            : 'inherit'
                                                    }
                                                >
                                                    {formatDisplayDate(
                                                        task.dueDate
                                                    )}
                                                </DateCell>
                                                <MuiTableCell
                                                    align={
                                                        theme.direction ===
                                                        'rtl'
                                                            ? 'right'
                                                            : 'inherit'
                                                    }
                                                    style={{
                                                        textDecoration:
                                                            task.isCompleted
                                                                ? 'line-through'
                                                                : 'none',
                                                        fontSize: '0.8125rem',
                                                    }}
                                                >
                                                    {task.title}
                                                </MuiTableCell>
                                                <MuiTableCell
                                                    align="right"
                                                    style={{
                                                        fontSize: '0.8125rem',
                                                    }}
                                                >
                                                    {task.duration}
                                                </MuiTableCell>
                                                <ActionsCell padding="checkbox">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) =>
                                                            handleMenuOpen(
                                                                e,
                                                                task.id
                                                            )
                                                        }
                                                        disabled={
                                                            task.isCompleted ||
                                                            isEditMode
                                                        }
                                                        sx={{
                                                            padding: 0,
                                                            width: '22px',
                                                            height: '22px',
                                                            opacity:
                                                                task.isCompleted ||
                                                                isEditMode
                                                                    ? 0.3
                                                                    : 1,
                                                        }}
                                                    >
                                                        <Icon
                                                            name="MoreVertIcon"
                                                            size={16}
                                                        />
                                                    </IconButton>
                                                </ActionsCell>
                                            </StyledTableRow>
                                        ))}
                                </MuiTableBody>
                            </MuiTable>
                        </Form>
                    ) : (
                        <StyledTasksEmptyState>
                            <EmptyTasks isArchive={isArchive} />
                        </StyledTasksEmptyState>
                    )}
                    {process.env.VITE_ENV_TYPE === EnvTypes.LOCAL && (
                        <DevTool control={methods.control} />
                    )}
                </TasksTableContainer>
            </Grid>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEditTask}>
                    {t('dashboardPage.tasksEdit')}
                </MenuItem>
                <MenuItem onClick={handleDeleteTask}>
                    {t('dashboardPage.tasksDelete')}
                </MenuItem>
            </Menu>
        </StyledCard>
    );
};

export default TasksCard;
